/**
 * AnonymityToggleButton — inline cloak/uncloak control for an Orchist
 * the caller has invited.
 *
 * Doctrine (sacred/anonymous-invitations.md §III + MX2 amendment):
 *   The original inviter may post-hoc toggle whether their identity is
 *   cloaked from THIS specific invitee. Calls
 *   `core.toggle_invitation_anonymity(p_invitee_contact_id)`, which
 *   atomically:
 *     1. Flips `Contact.invitationAnonymous` on the invitee row.
 *     2. Syncs the inviter→invitee `OrchistEmailShare` row in lockstep:
 *        cloaking revokes, un-cloaking re-creates with current email.
 *
 * UI behavior:
 *   - Visual: small italic underline link, matches the rest of the
 *     contemplative typography. Verbs honor the doctrine: *"cloak"* /
 *     *"un-cloak"*.
 *   - Optimistic UX: button renders the loading state during the RPC
 *     and surfaces inline error if the call fails.
 *   - The caller is responsible for refreshing whatever data store
 *     reflects the toggled state (lineage, pending-invitees, email
 *     shares). Pass `onSuccess` to wire that refresh.
 *
 * Permission: the RPC refuses if the caller is not the original
 * inviter (42501). The button doesn't pre-check — the server enforces.
 */

import { useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface AnonymityToggleButtonProps {
  /** The invitee's Contact id (the row whose invitationAnonymous flips). */
  inviteeId: string;
  /** Current value — drives the button label. */
  isAnonymous: boolean;
  /** Called after a successful toggle so the caller can refresh state. */
  onSuccess?: () => void;
  /** Override the default Tailwind classes if a parent needs different sizing. */
  className?: string;
  /** Compact mode for tight surfaces (uses smaller text). */
  compact?: boolean;
}

export function AnonymityToggleButton({
  inviteeId,
  isAnonymous,
  onSuccess,
  className,
  compact = false,
}: AnonymityToggleButtonProps) {
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleClick() {
    if (pending) return;
    setPending(true);
    setErrorMessage(null);
    try {
      const { error } = await supabase
        .schema('core')
        .rpc('toggle_invitation_anonymity', {
          p_invitee_contact_id: inviteeId,
        });
      if (error) throw new Error(error.message);
      onSuccess?.();
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Toggle failed.');
    } finally {
      setPending(false);
    }
  }

  const label = pending
    ? '…'
    : isAnonymous
      ? 'un-cloak'
      : 'cloak';
  const tooltip = isAnonymous
    ? 'Reveal yourself as their inviter (re-enables email visibility too).'
    : 'Cloak your identity from this Orchist (revokes the inviter→invitee email share).';

  const sizeClass = compact ? 'text-[0.65rem]' : 'text-xs';

  return (
    <span className={className ?? ''}>
      <button
        type="button"
        onClick={handleClick}
        disabled={pending}
        title={tooltip}
        aria-label={tooltip}
        className={`${sizeClass} italic underline underline-offset-4 decoration-accent/30 hover:decoration-accent text-ink/60 hover:text-ink transition-colors disabled:opacity-50`}
      >
        {label}
      </button>
      {errorMessage && (
        <span
          className={`${sizeClass} text-destructive italic ml-2`}
          role="alert"
        >
          {errorMessage}
        </span>
      )}
    </span>
  );
}
